import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { firstOrNull } from 'common/utils/array';
import { reportEvent } from 'modules/campaigns/behavioral';

import { useVenueAdsFreeTrialConfig } from '../../ad-campaign/components/free-trials/useVenueAdsFreeTrialConfig';
import { useCampaignModuleContext } from '../../context';
import useCampaignsOptions from '../../hooks/useCampaignsOptions';
import {
  telemetryChooseCampaignInteractionClickCampaignSelection,
  useTelemetryChooseCampaignView,
} from '../../utils/telemetry/telemetry';

type NewCampaignProps = {
  campaignTypesAvailableToEdit: CampaignType[];
  onNewCampaign: (campaignType: CampaignType) => void;
  onCancel?: () => void;
};

const Wrapper = styled.div`
  margin-inline: auto;
  max-width: 640px;
  position: relative;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`;

const CampaignTypeOption = styled.label<{ $checked: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  gap: 16px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
  padding: 16px;

  ${({ $checked }) =>
    $checked &&
    css`
      border-color: ${colors.wolt100};
      box-shadow: inset 0 0 0 1px ${colors.wolt100}; /* Thicker border without affecting layout */
      background-color: ${cssThemedValue({ light: colors.wolt8, dark: colors.wolt8 })};
    `}

  ${({ $disabled }) =>
    $disabled
      ? css`
          cursor: not-allowed;

          span {
            color: ${cbSemanticCssVars.textTertiary};
          }

          img {
            opacity: 0.5;
          }
        `
      : css`
          cursor: pointer;

          &:hover {
            border-color: ${colors.wolt100};
          }
          &:focus-within {
            box-shadow: 0 0 0 1px ${colors.wolt100};
          }
          &:has(:focus-visible) {
            border-color: ${cbSemanticCssVars.a11yFocusIndicator};
            box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
          }
        `};
`;

const HiddenInput = styled.input`
  margin: 0;
  opacity: 0;
  outline: none;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  height: 1px;
  width: 1px;
`;

const OptionContent = styled.span`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionHeader = styled.span`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const OptionTitle = styled.span`
  ${typographyCss.Title1()}
  align-self: flex-end;
`;

const OptionDescription = styled.span`
  ${typographyCss.Body3()}
  color: ${cbSemanticCssVars.textSecondary};
`;

const ImageWrapper = styled.span<{ disabled?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
  width: 70px;
`;

const Image = styled.img`
  max-width: 54px;
`;

const StyledTag = styled(Tag)`
  margin: -8px;
`;

const StyledCTAWrapper = styled.div<{ $withBorder: boolean }>`
  position: static;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  left: 0;
  right: 0;
  bottom: unset;
  border: none;
  padding: 0 24px;

  ${({ $withBorder }) =>
    $withBorder &&
    css`
      padding: 20px 24px;
      border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px 24px;
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    position: sticky;
    bottom: 0;

    > button {
      flex: 1 1 50%;
    }
  }
`;

const getEventActionForCampaignType = (campaignType: CampaignType) => {
  switch (campaignType) {
    case CampaignType.DELIVERY_DISCOUNT:
      return 'CHOOSE_CAMPAIGN_NO_DELIVERY_FEE';
    case CampaignType.BASKET_DISCOUNT:
      return 'CHOOSE_CAMPAIGN_ORDER_VALUE_DISCOUNT';
    case CampaignType.ADS:
      return 'CHOOSE_CAMPAIGN_ADS';
    case CampaignType.ITEM_DISCOUNT:
      return 'CHOOSE_CAMPAIGN_ITEM_PROMOTIONS';
    case CampaignType.END_OF_DAY:
    default:
      return null;
  }
};

const getDefaultCampaign = (availableTypes?: CampaignType[]): CampaignType | undefined => {
  if (availableTypes && availableTypes.length === 1) {
    return availableTypes[0];
  }

  return;
};

const CampaignTypeSelector = ({ campaignTypesAvailableToEdit = [], onNewCampaign, onCancel }: NewCampaignProps) => {
  const { venues, merchantId, country, entityType } = useCampaignModuleContext();
  const venue = firstOrNull(venues);
  const venueId = venue?.id;

  const { config: adsFreeTrialConfig } = useVenueAdsFreeTrialConfig({
    merchantId,
    venueId,
    country,
    city: venue?.city,
    productLine: venue?.product_line,
    enabled: !!venueId && entityType === 'venue',
  });

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignType | undefined>(
    getDefaultCampaign(campaignTypesAvailableToEdit),
  );
  const { campaigns } = useCampaignsOptions();

  const handleGetStartedClick = () => {
    if (!selectedCampaign) {
      return;
    }

    onNewCampaign(selectedCampaign);
    telemetryChooseCampaignInteractionClickCampaignSelection(selectedCampaign);
    reportEvent('CHOOSE_CAMPAIGN_GET_STARTED');
  };

  useTelemetryChooseCampaignView();

  return (
    <Wrapper data-test-id="campaign-type-selector">
      <OptionsWrapper>
        {campaigns.map(campaign => {
          const isAvailable = campaignTypesAvailableToEdit.includes(campaign.type);
          const selected = campaign.type === selectedCampaign;

          if (campaign.type === CampaignType.END_OF_DAY && !isAvailable) {
            return null;
          }

          const handleCampignTypeChange = () => {
            const eventAction = getEventActionForCampaignType(campaign.type);
            setSelectedCampaign(campaign.type);
            if (eventAction !== null) {
              reportEvent(eventAction);
            }
          };

          const hasAdsTag = !!adsFreeTrialConfig && campaign.type === CampaignType.ADS;
          const hasNormalTag = !!campaign.tag;
          const tag = hasAdsTag ? (
            <FormattedMessage
              id="campaigns.ads.free-trials.amount-tag"
              values={{
                amount: adsFreeTrialConfig?.formattedTotalBudgetWithCurrency ?? 0,
              }}
            />
          ) : hasNormalTag ? (
            campaign.tag
          ) : null;

          return (
            <CampaignTypeOption
              data-test-id={campaign.type}
              key={campaign.type}
              $checked={selected}
              $disabled={!isAvailable}
            >
              <HiddenInput
                type="radio"
                name="campaign-type"
                value={campaign.type}
                checked={selected}
                disabled={!isAvailable}
                onChange={handleCampignTypeChange}
              />

              <ImageWrapper>
                <Image src={campaign.image} alt="" aria-hidden="true" />
              </ImageWrapper>

              <OptionContent>
                <OptionHeader>
                  <OptionTitle>{campaign.title}</OptionTitle>
                  {isAvailable ? (
                    tag && (
                      <StyledTag variant="blue" size="tiny" uppercase data-test-id="option-tag">
                        {tag}
                      </StyledTag>
                    )
                  ) : (
                    <StyledTag variant="lightBlue" size="tiny" uppercase data-test-id="option-tag">
                      <FormattedMessage id="campaigns.coming-soon" />
                    </StyledTag>
                  )}
                </OptionHeader>
                <OptionDescription>{campaign.description}</OptionDescription>
              </OptionContent>
            </CampaignTypeOption>
          );
        })}
      </OptionsWrapper>
      <StyledCTAWrapper $withBorder={!!onCancel}>
        {!!onCancel && (
          <Button variant="secondary" size="small" onClick={onCancel}>
            <FormattedMessage id="state.cancel" />
          </Button>
        )}
        {campaignTypesAvailableToEdit.length > 0 && (
          <Button variant="primary" size="small" onClick={handleGetStartedClick} disabled={!selectedCampaign}>
            <FormattedMessage id="campaigns.get-started" />
          </Button>
        )}
      </StyledCTAWrapper>
    </Wrapper>
  );
};

export default CampaignTypeSelector;
